/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-07-23
 */

// Add third-party dependencies.
import { createApi } from '@reduxjs/toolkit/query/react';

// Add local dependencies.
import { baseQueryWithReauth } from '../../api/baseQuery';
import {
  SubscriptionPlan,
  UserSubscription,
  SubscriptionUsage,
  SelectPlanRequest,
} from '../../types/subscription';

// Define types for registration
export interface RegisterRequest {
  email: string;
  first_name: string;
  last_name: string;
  phone_number?: string;
  password: string;
  password_confirm: string;
  timezone?: string;
  language?: string;
  terms_accepted: boolean;
  privacy_accepted: boolean;
  marketing_emails?: boolean;
  turnstile_token: string;
}

// Define types for address management
export interface Address {
  street_address_1: string;
  street_address_2?: string;
  city: string;
  state_province: string;
  postal_code?: string;
  country: string;
}

// Define types for user profile
export interface UserProfile {
  bio?: string;
  avatar?: string;
  avatar_url?: string;
  has_avatar?: boolean;
  title?: string;
  company?: string;
  website?: string;
  linkedin?: string;
  instagram?: string;
  facebook?: string;
  email_notifications?: boolean;
  sms_notifications?: boolean;
  push_notifications?: boolean;
  address?: Address | null;
  has_address?: boolean;
  address_visible?: boolean;
  email_visible?: boolean;
  phone_visible?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface User {
  id: string;
  email: string;
  username?: string;
  first_name: string;
  last_name: string;
  full_name?: string;
  phone_number?: string;
  email_verified: boolean;
  email_verified_at?: string;
  phone_verified: boolean;
  phone_verified_at?: string;
  timezone: string;
  language: string;
  is_active: boolean;
  is_staff?: boolean;
  created_at: string;
  updated_at: string;
  terms_accepted: boolean;
  terms_accepted_at?: string;
  privacy_accepted: boolean;
  privacy_accepted_at?: string;
  marketing_emails: boolean;
  // Profile data (nested structure from sessionStatus)
  profile?: UserProfile;
  // Flattened fields (from UnifiedUserProfileSerializer response)
  bio?: string;
  avatar?: string;
  avatar_url?: string;
  has_avatar?: boolean;
  title?: string;
  company?: string;
  website?: string;
  linkedin?: string;
  instagram?: string;
  facebook?: string;
  email_notifications?: boolean;
  sms_notifications?: boolean;
  push_notifications?: boolean;
  address?: Address | null;
  has_address?: boolean;
  address_visible?: boolean;
  email_visible?: boolean;
  phone_visible?: boolean;
}

// Define types for session-based authentication
export interface LoginRequest {
  email: string;
  password: string;
  remember_me?: boolean;
}

export interface SessionResponse {
  user: User;
  csrf_token: string;
}

export interface SessionStatusResponse {
  authenticated: boolean;
  user: User | null;
  csrf_token: string;
}

export interface CsrfResponse {
  csrf_token: string;
}

// Define types for email verification
export interface EmailVerificationRequest {
  email: string;
  turnstile_token: string;
  language?: string;
}

export interface EmailVerificationConfirm {
  token: string;
}

export interface EmailVerificationResponse {
  message: string;
  success: boolean;
}

// Define types for password reset
export interface PasswordResetRequest {
  email: string;
  turnstile_token: string;
  language?: string;
}

export interface PasswordResetConfirm {
  token: string;
  new_password: string;
  new_password_confirm: string;
}

export interface PasswordResetResponse {
  message: string;
}

// Define types for profile management
export interface UpdateProfileRequest {
  email: string;
  first_name: string;
  last_name: string;
  phone_number?: string;
  timezone?: string;
  language?: string;
  marketing_emails?: boolean;
  // Profile fields
  bio?: string;
  title?: string;
  company?: string;
  website?: string;
  linkedin?: string;
  instagram?: string;
  facebook?: string;
  // Address and privacy fields
  address?: Address | null;
  address_visible?: boolean;
  email_visible?: boolean;
  phone_visible?: boolean;
}

export interface ChangePasswordRequest {
  old_password: string;
  new_password: string;
  new_password_confirm: string;
}

export interface ProfileUpdateResponse {
  user: User;
  message: string;
}

export interface ChangePasswordResponse {
  message: string;
}

// Define types for avatar management
export interface AvatarUploadRequest {
  avatar: File;
}

export interface AvatarUploadResponse {
  message: string;
  avatar: {
    avatar_url: string;
    has_avatar: boolean;
  };
}

export interface AvatarInfoResponse {
  avatar_url?: string;
  has_avatar: boolean;
  file_size?: number;
  uploaded_at?: string;
}

export interface AvatarDeleteResponse {
  message: string;
  success: boolean;
}

// Create the auth API slice with session-based authentication
export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'User',
    'Session',
    'Avatar',
    'Profile',
    'UserData',
    'SubscriptionPlan',
    'UserSubscription',
    'SubscriptionUsage',
  ],
  endpoints: builder => ({
    // Session-based registration
    sessionRegister: builder.mutation<SessionResponse, RegisterRequest>({
      query: (credentials: RegisterRequest) => ({
        url: 'auth/session/register/',
        method: 'POST',
        body: credentials,
      }),
      invalidatesTags: ['User', 'Session'],
    }),
    // Session-based login
    sessionLogin: builder.mutation<SessionResponse, LoginRequest>({
      query: (credentials: LoginRequest) => ({
        url: 'auth/session/login/',
        method: 'POST',
        body: credentials,
      }),
      invalidatesTags: ['User', 'Session'],
    }),
    // Session logout
    sessionLogout: builder.mutation<void, void>({
      query: () => ({
        url: 'auth/session/logout/',
        method: 'POST',
      }),
      invalidatesTags: ['User', 'Session'],
    }),
    // Check session status
    sessionStatus: builder.query<SessionStatusResponse, void>({
      query: () => ({
        url: 'auth/session/status/',
        method: 'GET',
      }),
      providesTags: ['Session', 'User', 'Profile', 'UserData'],
    }),
    // Get CSRF token
    getCsrfToken: builder.query<CsrfResponse, void>({
      query: () => ({
        url: 'auth/csrf/',
        method: 'GET',
      }),
    }),
    // Email verification request
    emailVerificationRequest: builder.mutation<
      EmailVerificationResponse,
      EmailVerificationRequest
    >({
      query: (data: EmailVerificationRequest) => ({
        url: 'auth/email/verify/request/',
        method: 'POST',
        body: data,
      }),
    }),
    // Email verification confirm
    emailVerificationConfirm: builder.mutation<
      EmailVerificationResponse,
      EmailVerificationConfirm
    >({
      query: (data: EmailVerificationConfirm) => ({
        url: 'auth/email/verify/confirm/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['User', 'Session'],
    }),
    // Password reset request
    passwordResetRequest: builder.mutation<
      PasswordResetResponse,
      PasswordResetRequest
    >({
      query: (data: PasswordResetRequest) => ({
        url: 'auth/password/reset/',
        method: 'POST',
        body: data,
      }),
    }),
    // Password reset confirm
    passwordResetConfirm: builder.mutation<
      PasswordResetResponse,
      PasswordResetConfirm
    >({
      query: (data: PasswordResetConfirm) => ({
        url: 'auth/password/reset/confirm/',
        method: 'POST',
        body: data,
      }),
    }),
    // Update user profile
    updateProfile: builder.mutation<
      ProfileUpdateResponse,
      UpdateProfileRequest
    >({
      query: (data: UpdateProfileRequest) => ({
        url: 'users/profile/',
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['User', 'Session', 'Profile', 'UserData'],
      // Optimistic update for better UX
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        // Optimistically update the sessionStatus cache
        const patchResult = dispatch(
          authApi.util.updateQueryData('sessionStatus', undefined, draft => {
            if (draft.user) {
              // Update user fields
              // eslint-disable-next-line no-param-reassign
              draft.user.email = arg.email;
              // eslint-disable-next-line no-param-reassign
              draft.user.first_name = arg.first_name;
              // eslint-disable-next-line no-param-reassign
              draft.user.last_name = arg.last_name;
              // eslint-disable-next-line no-param-reassign
              draft.user.phone_number = arg.phone_number;
              // eslint-disable-next-line no-param-reassign
              draft.user.marketing_emails = arg.marketing_emails || false;

              // Update profile fields (both nested and flattened for compatibility)
              if (!draft.user.profile) {
                // eslint-disable-next-line no-param-reassign
                draft.user.profile = {};
              }

              // Update nested profile fields
              if (arg.bio !== undefined) {
                // eslint-disable-next-line no-param-reassign
                draft.user.profile.bio = arg.bio;
                // eslint-disable-next-line no-param-reassign
                draft.user.bio = arg.bio;
              }
              if (arg.title !== undefined) {
                // eslint-disable-next-line no-param-reassign
                draft.user.profile.title = arg.title;
                // eslint-disable-next-line no-param-reassign
                draft.user.title = arg.title;
              }
              if (arg.company !== undefined) {
                // eslint-disable-next-line no-param-reassign
                draft.user.profile.company = arg.company;
                // eslint-disable-next-line no-param-reassign
                draft.user.company = arg.company;
              }
              if (arg.website !== undefined) {
                // eslint-disable-next-line no-param-reassign
                draft.user.profile.website = arg.website;
                // eslint-disable-next-line no-param-reassign
                draft.user.website = arg.website;
              }
              if (arg.linkedin !== undefined) {
                // eslint-disable-next-line no-param-reassign
                draft.user.profile.linkedin = arg.linkedin;
                // eslint-disable-next-line no-param-reassign
                draft.user.linkedin = arg.linkedin;
              }
              if (arg.instagram !== undefined) {
                // eslint-disable-next-line no-param-reassign
                draft.user.profile.instagram = arg.instagram;
                // eslint-disable-next-line no-param-reassign
                draft.user.instagram = arg.instagram;
              }
              if (arg.facebook !== undefined) {
                // eslint-disable-next-line no-param-reassign
                draft.user.profile.facebook = arg.facebook;
                // eslint-disable-next-line no-param-reassign
                draft.user.facebook = arg.facebook;
              }
              if (arg.address !== undefined) {
                // eslint-disable-next-line no-param-reassign
                draft.user.profile.address = arg.address;
                // eslint-disable-next-line no-param-reassign
                draft.user.address = arg.address;
              }
              if (arg.address_visible !== undefined) {
                // eslint-disable-next-line no-param-reassign
                draft.user.profile.address_visible = arg.address_visible;
                // eslint-disable-next-line no-param-reassign
                draft.user.address_visible = arg.address_visible;
              }
              if (arg.email_visible !== undefined) {
                // eslint-disable-next-line no-param-reassign
                draft.user.profile.email_visible = arg.email_visible;
                // eslint-disable-next-line no-param-reassign
                draft.user.email_visible = arg.email_visible;
              }
              if (arg.phone_visible !== undefined) {
                // eslint-disable-next-line no-param-reassign
                draft.user.profile.phone_visible = arg.phone_visible;
                // eslint-disable-next-line no-param-reassign
                draft.user.phone_visible = arg.phone_visible;
              }
            }
          })
        );

        try {
          await queryFulfilled;
        } catch {
          // Revert the optimistic update on error
          patchResult.undo();
        }
      },
    }),
    // Change password
    changePassword: builder.mutation<
      ChangePasswordResponse,
      ChangePasswordRequest
    >({
      query: (data: ChangePasswordRequest) => ({
        url: 'users/password/change/',
        method: 'POST',
        body: data,
      }),
    }),
    // Avatar upload/update
    uploadAvatar: builder.mutation<AvatarUploadResponse, AvatarUploadRequest>({
      query: (data: AvatarUploadRequest) => {
        const formData = new FormData();
        formData.append('avatar', data.avatar);
        return {
          url: 'users/profile/avatar/',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['User', 'Session', 'Avatar'],
    }),
    // Get avatar information
    getAvatarInfo: builder.query<AvatarInfoResponse, void>({
      query: () => ({
        url: 'users/profile/avatar/info/',
        method: 'GET',
      }),
      providesTags: ['Avatar'],
    }),
    // Delete avatar
    deleteAvatar: builder.mutation<AvatarDeleteResponse, void>({
      query: () => ({
        url: 'users/profile/avatar/delete/',
        method: 'DELETE',
      }),
      invalidatesTags: ['User', 'Session', 'Avatar'],
    }),

    // Subscription API endpoints
    // Get all available subscription plans
    getSubscriptionPlans: builder.query<SubscriptionPlan[], void>({
      query: () => ({
        url: 'subscription/plans/',
        method: 'GET',
      }),
      providesTags: ['SubscriptionPlan'],
    }),

    // Get current user's subscription
    getCurrentSubscription: builder.query<UserSubscription, void>({
      query: () => ({
        url: 'subscription/',
        method: 'GET',
      }),
      providesTags: ['UserSubscription'],
    }),

    // Select/change subscription plan
    selectPlan: builder.mutation<
      UserSubscription,
      SelectPlanRequest & { isUpgrade?: boolean }
    >({
      query: data => ({
        url:
          data.isUpgrade !== false
            ? 'subscription/upgrade/'
            : 'subscription/downgrade/',
        method: 'POST',
        body: {
          plan_slug: data.plan_slug,
          billing_cycle: data.billing_cycle,
        },
      }),
      invalidatesTags: [
        'UserSubscription',
        'SubscriptionUsage',
        'User',
        'Session',
      ],
    }),

    // Get current subscription usage
    getSubscriptionUsage: builder.query<SubscriptionUsage, void>({
      query: () => ({
        url: 'subscription/usage/',
        method: 'GET',
      }),
      providesTags: ['SubscriptionUsage'],
    }),

    // Get comprehensive subscription status (includes trial information)
    getSubscriptionStatus: builder.query<any, void>({
      query: () => ({
        url: 'subscription/status/',
        method: 'GET',
      }),
      providesTags: ['UserSubscription', 'SubscriptionUsage'],
    }),

    // Get subscription limits
    getSubscriptionLimits: builder.query<any, void>({
      query: () => ({
        url: 'subscription/limits/',
        method: 'GET',
      }),
      providesTags: ['UserSubscription'],
    }),

    // Trial management endpoints
    getTrialStatus: builder.query<any, void>({
      query: () => ({
        url: 'subscription/trial/',
        method: 'GET',
      }),
      providesTags: ['UserSubscription'],
    }),

    startTrial: builder.mutation<any, { plan_slug: string }>({
      query: data => ({
        url: 'subscription/trial/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['UserSubscription', 'SubscriptionUsage'],
    }),

    cancelTrial: builder.mutation<any, void>({
      query: () => ({
        url: 'subscription/trial/',
        method: 'DELETE',
      }),
      invalidatesTags: ['UserSubscription', 'SubscriptionUsage'],
    }),
  }),
});

// Export hooks for usage in functional components
export const {
  useSessionRegisterMutation,
  useSessionLoginMutation,
  useSessionLogoutMutation,
  useSessionStatusQuery,
  useLazySessionStatusQuery,
  useGetCsrfTokenQuery,
  useLazyGetCsrfTokenQuery,
  useEmailVerificationRequestMutation,
  useEmailVerificationConfirmMutation,
  // Password reset hooks
  usePasswordResetRequestMutation,
  usePasswordResetConfirmMutation,
  // Profile management hooks
  useUpdateProfileMutation,
  useChangePasswordMutation,
  // Avatar management hooks
  useUploadAvatarMutation,
  useGetAvatarInfoQuery,
  useLazyGetAvatarInfoQuery,
  useDeleteAvatarMutation,
  // Subscription management hooks
  useGetSubscriptionPlansQuery,
  useLazyGetSubscriptionPlansQuery,
  useGetCurrentSubscriptionQuery,
  useLazyGetCurrentSubscriptionQuery,
  useSelectPlanMutation,
  useGetSubscriptionUsageQuery,
  useLazyGetSubscriptionUsageQuery,
  // Subscription status and limits
  useGetSubscriptionStatusQuery,
  useLazyGetSubscriptionStatusQuery,
  useGetSubscriptionLimitsQuery,
  useLazyGetSubscriptionLimitsQuery,
  // Trial management hooks
  useGetTrialStatusQuery,
  useLazyGetTrialStatusQuery,
  useStartTrialMutation,
  useCancelTrialMutation,
} = authApi;
